import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CheckIcon from 'assets/actionPlan/check.svg';
import CloseIcon from 'assets/actionPlan/close.svg';
import EditIcon from 'assets/actionPlan/edit.svg';
import VectorIcon from 'assets/actionPlan/vector.svg';
import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import Modal from 'common-ui/modal';
import loadIcon from 'common-ui/table/loading.svg';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  getAssignmentById,
  removeAssignment,
  updateAssignment,
} from '../../../features/actions/actionPlanActions';
import { getBonusSteps } from '../ActionPlanConstans';
import {
  AlertIcon,
  AmountBox,
  AmountBoxCurrency,
  AmountBoxTitle,
  AmountBoxValue,
  Badge,
  BonusBox,
  BonusBoxDesc,
  BonusBoxPercent,
  BonusBoxTitle,
  BonusDeductionGrid,
  ButtonWrapper,
  CardDuration,
  CardHeader,
  CardHeaderIcon,
  CheckBg,
  CloseImg,
  DetailsContainer,
  DetailsTitle,
  DetailsTopBlock,
  DurationContent,
  ErrorDate,
  FormWrapper,
  HeaderInfo,
  InfoGrid,
  InfoItem,
  InfoItemSpan,
  InfoItemText,
  KpiCard,
  KpiGrid,
  LoadContainer,
  LoadingIcon,
  SettingsSubTitle,
  SettingsTitle,
  TopBlockClose,
  TopBlockTitle,
} from './AssignmentDetails.styles';

dayjs.extend(utc);

const AssignmentDetails = ({ isOpen, onClose, assignmentId, onSuccess }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [modalType, setModalType] = useState(null);
  const [date, setDate] = useState(null);
  const [dateError, setDateError] = useState('');

  const isEmployee = data?.scope === 'EMPLOYEE';
  const bonusSteps = getBonusSteps(data);
  const tomorrow = dayjs().add(1, 'day').startOf('day');
  const isInactiveAssignment = data?.active === false;

  const canEditAssignment =
    data?.effectiveDate &&
    dayjs(data.effectiveDate).startOf('day').isAfter(dayjs().startOf('day'));

  useEffect(() => {
    if (isOpen && assignmentId) {
      setLoading(true);
      dispatch(getAssignmentById(assignmentId))
        .unwrap()
        .then((res) => setData(res))
        .catch((err) => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [isOpen, assignmentId, dispatch]);

  const handleCloseModal = () => {
    setModalType(null);
    setDate(null);
    setDateError('');
  };

  const handleSubmitRemove = async () => {
    if (!date) {
      setDateError('Date is required');
      return;
    }

    const selected = dayjs(date).startOf('day');
    const tomorrow = dayjs().add(1, 'day').startOf('day');

    if (selected.isBefore(tomorrow, 'day')) {
      setDateError('Effective date must be from tomorrow');
      return;
    }

    await dispatch(
      removeAssignment({
        id: assignmentId,
        effectiveDate: selected.utc(true).toISOString(),
      })
    );

    onSuccess?.();
    handleCloseModal();
    onClose();
  };

  return (
    <Modal
      key={modalType}
      isOpen={isOpen}
      onClose={onClose}
      width={modalType === 'effectiveDate' ? '448px' : '620px'}
      $centered
      footer={false}
    >
      {modalType === 'confirm' ? (
        <>
          <FormWrapper>
            <CheckBg>
              <AlertIcon src={CheckIcon} />
            </CheckBg>

            <SettingsTitle className="confirmation">Confirmation</SettingsTitle>

            <SettingsSubTitle className="confirmation-sub">
              {data?.scope === 'DEPARTMENT'
                ? 'Are you sure you want to unassign this department? Changes will apply from the effective date.'
                : 'Are you sure you want to unassign this employee? Changes will apply from the effective date.'}
            </SettingsSubTitle>

            <ButtonWrapper>
              <Button className="cancel" outlined onClick={handleCloseModal} width="45%">
                Cancel
              </Button>

              <Button
                className="confirm"
                secondary
                onClick={() => {
                  setModalType('effectiveDate');
                }}
                width="45%"
              >
                Confirm
              </Button>
            </ButtonWrapper>
          </FormWrapper>
        </>
      ) : modalType === 'effectiveDate' ? (
        <>
          <FormWrapper className="date-effective">
            <SettingsTitle className="confirmation date-title">Effective date</SettingsTitle>
            <CustomDatePicker
              value={date}
              minDate={tomorrow}
              onChange={(val) => {
                setDate(val);
                setDateError('');
              }}
              slotProps={{
                actionBar: {
                  actions: [],
                },
              }}
            />

            {dateError && (
              <ErrorDate>
                <AlertIcon src={VectorIcon} />
                {dateError}
              </ErrorDate>
            )}

            <ButtonWrapper className="margin">
              <Button className="cancel" outlined onClick={handleCloseModal}>
                Cancel
              </Button>

              <Button className="confirm" primary onClick={handleSubmitRemove}>
                Submit
              </Button>
            </ButtonWrapper>
          </FormWrapper>
        </>
      ) : (
        <>
          {loading ? (
            <LoadContainer>
              <LoadingIcon src={loadIcon} alt="Loading..." />
            </LoadContainer>
          ) : data ? (
            <DetailsContainer>
              <DetailsTopBlock>
                <TopBlockTitle>Assignment Details</TopBlockTitle>

                <TopBlockClose>
                  <img
                    src={EditIcon}
                    alt="edit"
                    style={{
                      opacity: canEditAssignment ? 1 : 0.4,
                      cursor: canEditAssignment ? 'pointer' : 'not-allowed',
                    }}
                    onClick={() => {
                      if (!canEditAssignment) return;

                      navigate(`/action-plan/edit-assignment/${assignmentId}`);
                    }}
                  />
                </TopBlockClose>
              </DetailsTopBlock>
              <HeaderInfo>
                <DetailsTitle>{data.assigneeName || 'N/A'}</DetailsTitle>
                <InfoGrid>
                  <InfoItem>
                    <InfoItemSpan>Action Type</InfoItemSpan>
                    <Badge>{data.actionType || 'Primary'}</Badge>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemSpan>Effective Date</InfoItemSpan>
                    <InfoItemText>
                      {data.effectiveDate
                        ? dayjs(data.effectiveDate).format('YYYY-MM-DD')
                        : 'N/A'}
                    </InfoItemText>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemSpan>Estimation Type</InfoItemSpan>
                    <InfoItemText style={{ textTransform: 'capitalize' }}>
                      {data.estimationType?.toLowerCase() || 'N/A'}
                    </InfoItemText>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemSpan>Duration</InfoItemSpan>
                    <InfoItemText style={{ textTransform: 'capitalize' }}>
                      {data.kpiConfig?.periodType?.toLowerCase() || 'N/A'}
                    </InfoItemText>
                  </InfoItem>
                </InfoGrid>
              </HeaderInfo>

              {(data.estimationType === 'FINANCIAL' || data.estimationType === 'BOTH') && (
                <KpiCard type="financial">
                  <CardHeader>
                    <CardHeaderIcon>$</CardHeaderIcon> Financial KPI
                  </CardHeader>
                  <CardDuration>
                    Duration:
                    <DurationContent>{data.kpiConfig?.periodType || 'N/A'}</DurationContent>
                  </CardDuration>
                  <KpiGrid>
                    <AmountBox>
                      <AmountBoxTitle className="red">Low Amount</AmountBoxTitle>
                      <AmountBoxValue>
                        {data.kpiConfig?.financialLow?.toLocaleString() || 0}
                      </AmountBoxValue>
                      <AmountBoxCurrency>AMD</AmountBoxCurrency>
                    </AmountBox>

                    <AmountBox>
                      <AmountBoxTitle className="blue">Target Amount</AmountBoxTitle>
                      <AmountBoxValue>
                        {data.kpiConfig?.financialTarget?.toLocaleString() || 0}
                      </AmountBoxValue>
                      <AmountBoxCurrency>AMD</AmountBoxCurrency>
                    </AmountBox>

                    <AmountBox>
                      <AmountBoxTitle className="green">High Amount</AmountBoxTitle>
                      <AmountBoxValue>
                        {data.kpiConfig?.financialHigh?.toLocaleString() || 0}
                      </AmountBoxValue>
                      <AmountBoxCurrency>AMD</AmountBoxCurrency>
                    </AmountBox>
                  </KpiGrid>
                </KpiCard>
              )}

              {(data.estimationType === 'QUANTITY' || data.estimationType === 'BOTH') && (
                <KpiCard type="quantity">
                  <CardHeader>
                    <CardHeaderIcon className="icon">#</CardHeaderIcon> Quantity KPI
                  </CardHeader>
                  <CardDuration>
                    Duration:
                    <DurationContent>{data.kpiConfig?.periodType || 'N/A'}</DurationContent>
                  </CardDuration>
                  <KpiGrid>
                    <AmountBox>
                      <AmountBoxTitle className="red">Low Count</AmountBoxTitle>
                      <AmountBoxValue>{data.kpiConfig?.quantityLow || 0}</AmountBoxValue>
                    </AmountBox>

                    <AmountBox>
                      <AmountBoxTitle className="blue">Target Count</AmountBoxTitle>
                      <AmountBoxValue>{data.kpiConfig?.quantityTarget || 0}</AmountBoxValue>
                    </AmountBox>

                    <AmountBox>
                      <AmountBoxTitle className="green">High Count</AmountBoxTitle>
                      <AmountBoxValue>{data.kpiConfig?.quantityHigh || 0}</AmountBoxValue>
                    </AmountBox>
                  </KpiGrid>
                </KpiCard>
              )}

              {isEmployee && (
                <BonusDeductionGrid>
                  {bonusSteps.map((step, index) => (
                    <BonusBox key={step.key} type={step.type}>
                      <BonusBoxTitle>{step.title}</BonusBoxTitle>
                      <BonusBoxPercent index={index}>
                        {step.prefix}
                        {step.value ?? 0}%
                      </BonusBoxPercent>
                      <BonusBoxDesc>{step.desc}</BonusBoxDesc>
                    </BonusBox>
                  ))}
                </BonusDeductionGrid>
              )}

              <ButtonWrapper>
                <Button type="secondary" onClick={onClose} width="100%">
                  Close
                </Button>
                <Button
                  type="primary"
                  onClick={() => !isInactiveAssignment && setModalType('confirm')}
                  className="remove"
                  width="100%"
                  disabled={isInactiveAssignment}
                >
                  Remove
                </Button>
              </ButtonWrapper>
            </DetailsContainer>
          ) : (
            <div>No data found.</div>
          )}
        </>
      )}
    </Modal>
  );
};

export default AssignmentDetails;
